import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Minus, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart, formatHuf } from "../context/CartContext";

export default function CartDrawer() {
  const { items, subtotal, totalQty, drawerOpen, closeDrawer, updateQty, removeItem } = useCart();

  // Lock body scroll when open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === "Escape" && closeDrawer();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  return (
    <div
      data-testid="cart-drawer-root"
      aria-hidden={!drawerOpen}
      className={`fixed inset-0 z-[80] ${drawerOpen ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        data-testid="cart-backdrop"
        className={`absolute inset-0 bg-black/55 backdrop-blur-[2px] transition-opacity duration-500 ${
          drawerOpen ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Panel */}
      <aside
        data-testid="cart-panel"
        className={`absolute top-0 right-0 h-full w-full sm:w-[460px] md:w-[520px] bg-[color:var(--he-bg)] text-[color:var(--he-ink)] shadow-2xl flex flex-col transition-transform duration-500 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-6 border-b border-[color:var(--he-line)]">
          <div className="flex items-baseline gap-3">
            <p className="overline">Kosár</p>
            <span className="figure italic text-[color:var(--he-gold)] text-xl">
              {String(totalQty).padStart(2, "0")}
            </span>
          </div>
          <button
            onClick={closeDrawer}
            data-testid="cart-close"
            aria-label="Kosár bezárása"
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-[color:var(--he-cream)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-7 py-7">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[color:var(--he-gold)]/50 mb-7">
                <ShoppingBag size={18} className="text-[color:var(--he-gold)]" strokeWidth={1.5} />
              </span>
              <p className="overline-xs text-[color:var(--he-ink-soft)] mb-3">Üres a kosár</p>
              <h3 className="font-serif-display text-3xl leading-[1.1]">
                Még nincs <span className="italic text-[color:var(--he-gold)]">termék a kosárban.</span>
              </h3>
              <p className="text-sm text-[color:var(--he-ink-soft)] font-light mt-5 max-w-xs">
                Válaszd ki, melyik tinks tetszik — minden tétel egyedi, és ami a képen van, az érkezik.
              </p>
              <Link
                to="/termekek"
                onClick={closeDrawer}
                data-testid="cart-empty-cta"
                className="mt-10 btn-outline-gold"
              >
                <span>Termékek</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--he-line)]">
              {items.map((it) => (
                <li key={it.key} data-testid={`cart-item-${it.key}`} className="flex gap-5 py-6">
                  <div className="w-24 h-32 flex-shrink-0 overflow-hidden bg-[color:var(--he-cream)]">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col min-w-0">
                    <p className="overline-xs text-[color:var(--he-ink-soft)] truncate">
                      {it.origin === "europai" ? "Európai" : "Kazahsztáni"} · {it.length} · {it.weight}
                    </p>
                    <h4 className="font-serif-display text-lg leading-tight mt-1 mb-2">
                      {it.name}
                    </h4>
                    <p className="text-sm font-light text-[color:var(--he-ink-soft)]">
                      {formatHuf(it.unitPrice)} / db
                    </p>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="flex items-center border border-[color:var(--he-line)] rounded-full">
                        <button
                          onClick={() => updateQty(it.key, it.qty - 1)}
                          data-testid={`cart-decrease-${it.productId}`}
                          aria-label="Csökkentés"
                          className="px-3 py-1.5 hover:text-[color:var(--he-gold)]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm font-serif-display">{it.qty}</span>
                        <button
                          onClick={() => updateQty(it.key, it.qty + 1)}
                          data-testid={`cart-increase-${it.productId}`}
                          aria-label="Növelés"
                          className="px-3 py-1.5 hover:text-[color:var(--he-gold)]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.key)}
                        data-testid={`cart-remove-${it.productId}`}
                        className="overline-xs text-[color:var(--he-ink-soft)] hover:text-[color:var(--he-gold)] transition-colors"
                      >
                        Törlés
                      </button>
                    </div>
                  </div>
                  <div className="text-right font-serif-display text-base whitespace-nowrap">
                    {formatHuf(it.unitPrice * it.qty)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[color:var(--he-line)] px-7 py-6 bg-[color:var(--he-bg-alt)]">
            <div className="flex items-baseline justify-between mb-2">
              <p className="overline-xs text-[color:var(--he-ink-soft)]">Részösszeg</p>
              <p className="font-serif-display text-2xl" data-testid="cart-subtotal">
                {formatHuf(subtotal)}
              </p>
            </div>
            <p className="text-xs text-[color:var(--he-ink-soft)] font-light mb-5">
              A szállítási díj a következő lépésben kerül kalkulálásra.
            </p>
            <Link
              to="/penztar"
              onClick={closeDrawer}
              data-testid="cart-checkout"
              className="btn-ink w-full justify-center"
            >
              <span>Pénztárhoz</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}
